import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@constants'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'

function PhoneVerificationPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [timeLeft, setTimeLeft] = useState(60)
  const [canResend, setCanResend] = useState(false)

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [timeLeft])

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    
    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus sur le champ suivant
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`)
      if (nextInput) nextInput.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const verificationCode = code.join('')
    
    if (verificationCode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres')
      return
    }

    setLoading(true)

    try {
      // Simulation de vérification
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Simuler connexion réussie
      login({ name: 'Utilisateur' }, 'fake-token')
      toast.success('Code vérifié avec succès !')
      navigate(ROUTES.DASHBOARD)
    } catch (err) {
      toast.error('Code incorrect. Veuillez réessayer.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!canResend) return
    
    setLoading(true)
    try {
      // Simulation d'envoi de code
      await new Promise(resolve => setTimeout(resolve, 1000))
      setTimeLeft(60)
      setCanResend(false)
      toast.success('Code renvoyé avec succès !')
    } catch (err) {
      toast.error('Erreur lors de l\'envoi du code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-md mx-auto animate-slide-up">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">📱</span>
        </div>
        <h2 className="text-2xl font-bold text-darkBlue mb-2">
          Vérification du téléphone
        </h2>
        <p className="text-gray-600">
          Nous avons envoyé un code de confirmation à 6 chiffres à votre numéro de téléphone.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
            Entrez le code de confirmation
          </label>
          <div className="flex gap-3 justify-center">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-200 outline-none transition-all"
              />
            ))}
          </div>
        </div>

        <button 
          type="submit" 
          className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed text-darkBlue font-bold" 
          disabled={loading || code.some(d => !d)}
        >
          {loading ? 'Vérification...' : 'Vérifier'}
        </button>
      </form>

      <div className="mt-6 text-center">
        {canResend ? (
          <button
            onClick={handleResend}
            disabled={loading}
            className="text-primary font-semibold hover:text-primary-600 disabled:opacity-50"
          >
            Renvoyer le code
          </button>
        ) : (
          <p className="text-gray-600">
            Renvoyer le code dans <span className="font-bold text-primary">{timeLeft}s</span>
          </p>
        )}
      </div>

      <div className="mt-4 text-center">
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="text-gray-600 hover:text-gray-900"
        >
          ← Retour à la connexion
        </button>
      </div>
    </div>
  )
}

export default PhoneVerificationPage
